import {
  messageAdminService,
  AdminConversation,
  AdminMessage,
} from './message-admin.service';

interface CachedConversations {
  id: string;
  data: string;
  cachedAt: number;
}

interface CachedMessages {
  projectId: string;
  data: string;
  cachedAt: number;
}

const CONVERSATIONS_KEY = 'pwa_internal_cache_conversations';
const MESSAGES_PREFIX = 'pwa_internal_cache_messages:';

export const messageCacheService = {
  async cacheConversations(conversations: AdminConversation[]): Promise<void> {
    const payload: CachedConversations = {
      id: 'conversations',
      data: JSON.stringify(conversations),
      cachedAt: Date.now(),
    };
    try {
      localStorage.setItem(CONVERSATIONS_KEY, JSON.stringify(payload));
    } catch {
      // ignore
    }
  },

  async getCachedConversations(): Promise<AdminConversation[] | null> {
    try {
      const raw = localStorage.getItem(CONVERSATIONS_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as CachedConversations;
      return JSON.parse(parsed.data) as AdminConversation[];
    } catch {
      return null;
    }
  },

  async cacheMessages(projectId: string, messages: AdminMessage[]): Promise<void> {
    const payload: CachedMessages = {
      projectId,
      data: JSON.stringify(messages),
      cachedAt: Date.now(),
    };
    try {
      localStorage.setItem(`${MESSAGES_PREFIX}${projectId}`, JSON.stringify(payload));
    } catch {
      // ignore
    }
  },

  async getCachedMessages(projectId: string): Promise<AdminMessage[] | null> {
    try {
      const raw = localStorage.getItem(`${MESSAGES_PREFIX}${projectId}`);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as CachedMessages;
      return JSON.parse(parsed.data) as AdminMessage[];
    } catch {
      return null;
    }
  },

  async loadConversations(): Promise<AdminConversation[]> {
    try {
      const data = await messageAdminService.listConversations();
      await this.cacheConversations(data);
      return data;
    } catch (error) {
      const cached = await this.getCachedConversations();
      if (cached) return cached;
      throw error;
    }
  },

  async loadMessages(projectId: string): Promise<{
    messages: AdminMessage[];
    clientId: string;
  }> {
    try {
      const data = await messageAdminService.listMessages(projectId);
      await this.cacheMessages(projectId, data.messages);
      return data;
    } catch (error) {
      const cached = await this.getCachedMessages(projectId);
      if (cached) {
        return { messages: cached, clientId: '' };
      }
      throw error;
    }
  },
};